import Box from '@mui/material/Box/Box'
import Checkbox from '@mui/material/Checkbox/Checkbox'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Collapse from '@mui/material/Collapse/Collapse'
import { ReactComponent as NextIcon } from 'assets/arrow_close_logo.svg'
import { ReactComponent as CheckBox } from 'assets/icons/checkBox.svg'
import CheckBoxActive from 'assets/icons/checkBoxActive'
import { ReactComponent as PlusIcon } from 'assets/plan_edit_plus_logo.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import { IRationTableComponents } from 'models/IRationSystem'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import { useAppDispatch } from 'store/hooks'
import { addComponentToRation } from 'store/rationSystemSlice'
import { COLORS } from 'styles/colors'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { IInputData } from '../AddNewRation'
import PopupRationAddComp from 'components/shared/PopupRationAddComp'

interface PopupListComponentsProps {
	rationComponents: IRationTableComponents[]
	setActiveComponentIds: any
	activeComponentIds: number[]
	setRationData?: Dispatch<SetStateAction<IInputData>>
	editComp?: boolean
	indexForRationArray?: number
}

const PopupListComponents = ({
	rationComponents,
	setActiveComponentIds,
	activeComponentIds,
	setRationData,
	editComp,
	indexForRationArray,
}: PopupListComponentsProps) => {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [openPopupAddComp, setOpenPopupAddComp] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const dispatch = useAppDispatch()
	const normalizedQuery = query.trim().toLowerCase()
	const visibleComponents = (rationComponents ?? []).filter(component =>
		component.title.toLowerCase().includes(normalizedQuery),
	)

	const addComponent = (component: IRationTableComponents) => {
		if (activeComponentIds.includes(component.id)) return

		setActiveComponentIds(prevIds => [...prevIds, component.id])
		if (editComp === true) {
			dispatch(
				addComponentToRation({
					id: component.id,
					index: indexForRationArray,
				}),
			)
			return
		}

		setRationData?.(prevData => ({
			...prevData,
			components: [
				...(prevData.components || []),
				{
					...component,
					weight_per_head: '',
					dry_matter_kg: '',
				},
			],
		}))
	}

	const toggleComponent = (component: IRationTableComponents) => {
		if (activeComponentIds.includes(component.id)) {
			setActiveComponentIds(prevIds =>
				prevIds.filter(id => id !== component.id),
			)
			if (editComp === true) {
				dispatch(
					addComponentToRation({
						id: component.id,
						index: indexForRationArray,
					}),
				)
				return
			}
			setRationData?.(prevData => ({
				...prevData,
				components: (prevData.components || []).filter(
					item => item.id !== component.id,
				),
			}))
			return
		}

		addComponent(component)
	}

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				<Box
					onClick={() => {
						setOpen(true)
						inputRef.current?.focus()
					}}
					sx={{
						width: '400px',
						height: '54px',
						backgroundColor: '#fff',
						borderRadius: '12px',
						border: `1px solid ${COLORS.inputBorder}`,
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '8px',
						padding: '16px',
						marginBottom: '4px',
						boxShadow: open && '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
						cursor: 'text',
					}}
				>
					<SearchIcon stroke={COLORS.icon} />
					<Box
						component='input'
						ref={inputRef}
						value={query}
						placeholder='Добавить 1 или несколько компонентов'
						onChange={event => {
							setQuery(event.target.value)
							setOpen(true)
						}}
						onFocus={() => setOpen(true)}
						onClick={event => event.stopPropagation()}
						sx={{
							flex: 1,
							minWidth: 0,
							border: 'none',
							outline: 'none',
							background: 'transparent',
							padding: 0,
							fontFamily: 'inherit',
							fontSize: '14px',
							lineHeight: '17px',
							color: COLORS.black,
							'&::placeholder': {
								color: COLORS.secondaryFont,
								opacity: 1,
							},
						}}
					/>
					<Box
						onClick={event => {
							event.stopPropagation()
							setOpen(prevOpen => !prevOpen)
						}}
						sx={{ cursor: 'pointer', display: 'flex' }}
					>
						<NextIcon
							stroke={COLORS.icon}
							style={{ transform: open ? 'rotate(-180deg)' : undefined }}
						/>
					</Box>
				</Box>
				<Collapse in={open} timeout='auto'>
					<Box sx={{ position: 'relative' }}>
						<Box
							sx={{
								zIndex: '10',
								height: '210px',
								width: '400px',
								position: 'absolute',
								backgroundColor: '#fff',
								borderRadius: '12px',
								border: `1px solid ${COLORS.inputBorder}`,
								padding: '16px',
								boxShadow: open && '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
								overflow: 'auto',
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
								paddingBottom: '40px',
								'&::-webkit-scrollbar': {
									width: 4,
									marginLeft: '20px',
								},
								'&::-webkit-scrollbar-track': {
									backgroundColor: '#DBDBDB',
									borderRadius: 12,
									margin: 1.2,
								},
								'&::-webkit-scrollbar-thumb': {
									backgroundColor: COLORS.icon,
									borderRadius: 12,
								},
							}}
						>
							<TypographySecondary>
								Компонентов: {visibleComponents.length}
							</TypographySecondary>
							{visibleComponents.map(el => (
								<Box
									key={el.id}
									onClick={() => toggleComponent(el)}
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'flex-start',
										gap: '8px',
										cursor: 'pointer',
									}}
								>
									<Checkbox
										sx={{
											color: COLORS.inputBorder,
											'&.MuiCheckbox-root': {
												margin: '0px',
												padding: '0px',
												width: '20px',
												height: '20px',
												'&.Mui-checked': {
													color: COLORS.main,
												},
											},
										}}
										disableRipple
										icon={<CheckBox />}
										checkedIcon={<CheckBoxActive />}
										checked={activeComponentIds.includes(el.id)}
										onClick={event => event.stopPropagation()}
										onChange={() => toggleComponent(el)}
									/>
									<Box display='flex' flexDirection='column'>
										<TypographyPrimary>{el.title}</TypographyPrimary>
										<TypographySecondary>
											{el.dry_matter_percent} СВ,%
										</TypographySecondary>
									</Box>
								</Box>
							))}
						</Box>
						<Box
							onClick={() => setOpenPopupAddComp(true)}
							sx={{
								zIndex: '11',
								position: 'absolute',
								top: '183px',
								left: '16px',
								width: '370px',
								height: '24px',
								backdropFilter: 'blur(2px)',
								borderRadius: '8px',
								background: 'rgba(255, 255, 255, 0.70)',
								display: 'flex',
								flexDirection: 'row',
								gap: '8px',
								alignItems: 'center',
								cursor: 'pointer',
							}}
						>
							<PlusIcon height='24px' width='24px' stroke={COLORS.secondary} />
							<TypographySecondary14>
								Добавить новый компонент
							</TypographySecondary14>
						</Box>
					</Box>
				</Collapse>
				<PopupRationAddComp
					openAddComp={openPopupAddComp}
					setOpenAddComp={setOpenPopupAddComp}
					onCreated={addComponent}
				/>
			</Box>
		</ClickAwayListener>
	)
}

export default PopupListComponents
